import { enTetesAuth } from './enTetesAuth';
/**
 * Service for communicating with the ColdFusion REST API
 * Now using @cabaza/shared for people-related operations
 */

// Re-export people functions from @cabaza/shared
export {
    getAllPeople,
    getPeopleByUuid,
    createPeople,
    updatePeople,
    deletePeople,
    updatePeopleCoordinates,
    getPeoplePreferences,
    savePeoplePreferences,
    getProfileStats,
    // Legacy aliases
    getPersonByUuid,
    getPersonById,
    createPerson,
    savePerson,
    updatePerson,
    deletePerson,
    updatePersonCoordinates,
} from '@cabaza/shared';

// previsualiserEffacement vit dans adminApiService, pas dans le paquet
// partage : elle etait listee ci-dessus par erreur, ce qui faisait echouer
// le build (« is not exported by @cabaza/shared »).
export { previsualiserEffacement } from './adminApiService';

// Re-export utilities that might be used elsewhere
export { normalizeKeys, toCamelCase, getValue } from '@cabaza/shared';

// Import for local use

// ========= PLACES API =========
// Places functions remain local as they're not in shared yet

const PLACES_API_URL = `${import.meta.env.VITE_API_BASE_URL}/places`;

/**
 * Helper function to handle API responses
 */
const handleResponse = async (response) => {
    const responseText = await response.text();

    let result;
    try {
        result = JSON.parse(responseText);
    } catch (parseError) {
        console.error('Failed to parse JSON:', parseError);
        throw new Error(`Invalid JSON response: ${responseText}`);
    }

    if (!response.ok) {
        const error = new Error(result.message || `HTTP error! status: ${response.status}`);
        error.status = response.status;
        error.data = result;
        throw error;
    }

    return result;
};

/**
 * Create a new place/enterprise (POST /places)
 * @param {Object} placeData - Place data to save
 * @returns {Promise<Object>} Created place with UUID
 */
export const createPlace = async (placeData) => {
    try {
        console.log('Creating place:', placeData);

        const response = await fetch(PLACES_API_URL, {
            method: 'POST',
            credentials: 'include',
            headers: enTetesAuth(),
            body: JSON.stringify(placeData)
        });

        const result = await handleResponse(response);
        console.log('Place created:', result);
        return result;
    } catch (error) {
        console.error('Error creating place:', error);
        throw error;
    }
};

// ========= REGISTRATION CHECKS =========

const PEOPLES_API_URL = 'https://apicabaza.creago.be/rest/cabaza/peoples';

/**
 * Check if an email already exists in the database
 * @param {string} email - Email to check
 * @returns {Promise<{exists: boolean}>}
 */
export const checkEmailExists = async (email) => {
    try {
        const response = await fetch(`${PEOPLES_API_URL}/check-email?email=${encodeURIComponent(email)}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json'
            }
        });

        const result = await handleResponse(response);
        return { exists: result.exists ?? result.EXISTS ?? false };
    } catch (error) {
        console.error('Error checking email:', error);
        // On error, don't block registration - let backend handle it
        return { exists: false };
    }
};

/**
 * Check if a profile already exists by name + postalCode OR city
 * @param {string} firstName
 * @param {string} lastName
 * @param {string} postalCode
 * @param {string} city
 * @returns {Promise<{exists: boolean}>}
 */
export const checkDuplicateProfile = async (firstName, lastName, postalCode, city) => {
    try {
        const response = await fetch(`${PEOPLES_API_URL}/check-duplicate`, {
            method: 'POST',
            headers: enTetesAuth(),
            body: JSON.stringify({ firstName, lastName, postalCode, city })
        });

        const result = await handleResponse(response);
        return { exists: result.exists ?? result.EXISTS ?? false };
    } catch (error) {
        console.error('Error checking duplicate profile:', error);
        // On error, don't block registration - let backend handle it
        return { exists: false };
    }
};
